import { am5 } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

export const LABEL_FONT_SIZE = 10;

/**
 * Measures the actual pixel width of text using Canvas API.
 */
const measureTextWidth = (text: string): number => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Fallback if canvas is unavailable
    return text.length * 8;
  }

  // Just some font settings as this won't vary much
  ctx.font = `${LABEL_FONT_SIZE}px Arial, sans-serif`;
  return ctx.measureText(text).width;
};

/**
 * Calculates the required width for axis labels based on the largest absolute value
 * in the last row of the data, formatted using am5's number formatter.
 * Uses Canvas measureText for accurate width calculation, accounting for download prop.
 */
export const calculateLabelWidth = ({
  root,
  data,
  xAxisField,
  download,
}: {
  root: am5.Root;
  data?: types.widgets.TableData;
  xAxisField?: string;
  download?: boolean;
}): number => {
  if (!data || !xAxisField || data.length === 0) {
    return 100;
  }

  const keys = Object.keys(data[0]).filter((key) => key !== xAxisField);
  if (keys.length === 0) {
    return 100;
  }

  const lastRow = data[data.length - 1];
  let maxAbsoluteValue = 0;

  keys.forEach((key) => {
    const value = lastRow[key];
    if (value !== null && value !== undefined) {
      const numValue = Math.abs(value as number);
      maxAbsoluteValue = Math.max(maxAbsoluteValue, numValue);
    }
  });

  if (maxAbsoluteValue === 0) {
    return 100;
  }

  const formatted = root.numberFormatter.format(maxAbsoluteValue);
  const textWithSpace = ` ${formatted} `;

  const textWidth = measureTextWidth(textWithSpace);

  // Label padding from create-series.tsx: paddingLeft: 2, paddingRight: 5
  const labelPadding = 2 + 5; // 7px total horizontal padding

  // Account for label positioning offset (centerX percentage can push label to the right)
  // The label is positioned at centerX: am5.percent(cx) where cx = Math.min(50, formatted.length * 2)
  // Minimal buffer to prevent cut-off while keeping labels positioned more to the right
  const positioningBuffer = 5;

  // Base width calculation: text + padding + minimal buffers
  const baseWidth = textWidth + labelPadding + positioningBuffer;

  // Minimal width to prevent label cut-off while keeping labels positioned more to the right
  // Higher minimum for download mode to prevent offscreen labels
  const minWidth = download ? 140 : 100;

  return Math.max(baseWidth * 2, minWidth);
};
